import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ReportProblemModal from "@/components/ReportProblemModal";
import { Package, Truck, CheckCircle2, XCircle, ChevronRight, ArrowLeft, ExternalLink, Clock, AlertTriangle, FileText, DollarSign, ClipboardList, Ban } from "lucide-react";

const STAGE_META = {
  paid: { label: "Order Placed", icon: Package, tone: "amber" },
  packed: { label: "Packing", icon: Package, tone: "amber" },
  shipped: { label: "Shipped", icon: Truck, tone: "sky" },
  delivered: { label: "Delivered", icon: CheckCircle2, tone: "emerald" },
  cancelled: { label: "Cancelled", icon: XCircle, tone: "rose" },
};

// Cancellation-flow stage meta — 3 steps that replace the tracker when a
// cancellation request is open.
const CANCEL_STAGE_META = {
  requested: { label: "Requested", icon: ClipboardList },
  approved: { label: "Approved", icon: CheckCircle2 },
  refunded: { label: "Refunded", icon: DollarSign },
  denied: { label: "Declined", icon: Ban },
};

function StatusChip({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.paid;
  const Icon = m.icon;
  const tones = {
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    sky: "bg-sky-50 text-sky-800 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${tones[m.tone]}`} data-testid={`status-chip-${stage}`}>
      <Icon size={12}/> {m.label}
    </span>
  );
}

function Tracker({ current }) {
  const steps = ["paid", "packed", "shipped", "delivered"];
  if (current === "cancelled") {
    return (
      <div className="flex items-center gap-2 text-sm text-rose-800" data-testid="tracker-cancelled">
        <XCircle size={16}/> Order cancelled
      </div>
    );
  }
  const idx = steps.indexOf(current);
  return (
    <div className="flex items-center gap-2 w-full" data-testid="tracker">
      {steps.map((s, i) => {
        const m = STAGE_META[s];
        const reached = i <= idx;
        const Icon = m.icon;
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none" data-testid={`tracker-step-${s}`}>
            <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${reached ? "bg-[var(--ff-primary)] border-[var(--ff-primary)] text-white" : "bg-white border-stone-300 text-stone-400"}`}>
              <Icon size={16}/>
            </div>
            <div className="ml-2 text-xs whitespace-nowrap mr-2">
              <p className={reached ? "font-semibold" : "text-[var(--ff-text-muted)]"}>{m.label}</p>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? "bg-[var(--ff-primary)]" : "bg-stone-200"}`}/>}
          </div>
        );
      })}
    </div>
  );
}

function CancellationTracker({ cancellation }) {
  const steps = ["requested", "approved", "refunded"];
  const status = cancellation?.status;
  if (status === "denied") {
    return (
      <div className="flex items-start gap-2 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="cancel-tracker-denied">
        <Ban size={16} className="mt-0.5 flex-shrink-0"/>
        <div>
          <p className="font-semibold">Cancellation declined</p>
          {cancellation?.ops_note && <p className="text-xs mt-1 text-rose-700">{cancellation.ops_note}</p>}
        </div>
      </div>
    );
  }
  const idx = steps.indexOf(status);
  return (
    <div className="flex items-center gap-2 w-full" data-testid="cancel-tracker">
      {steps.map((s, i) => {
        const m = CANCEL_STAGE_META[s];
        const reached = i <= idx;
        const Icon = m.icon;
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none" data-testid={`cancel-step-${s}`}>
            <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${reached ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-stone-300 text-stone-400"}`}>
              <Icon size={16}/>
            </div>
            <div className="ml-2 text-xs whitespace-nowrap mr-2">
              <p className={reached ? "font-semibold" : "text-[var(--ff-text-muted)]"}>{m.label}</p>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? "bg-rose-600" : "bg-stone-200"}`}/>}
          </div>
        );
      })}
    </div>
  );
}

function CancelRequestModal({ open, onClose, orderId, onSuccess }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;
  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const r = await api.post(`/orders/${orderId}/cancel-request`, { reason });
      onSuccess?.(r.data.cancellation);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not open cancellation request.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cancel-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-rose-800">
          <AlertTriangle size={20}/>
          <p className="font-display text-2xl">Request cancellation</p>
        </div>
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="cancel-warning">
          We&apos;ll do our best to stop the shipment, but cancellations are <strong>not guaranteed</strong> once packing has begun. If we can&apos;t stop it, we&apos;ll help you sort it after delivery.
        </div>
        <label className="block text-sm mt-4 font-semibold">Reason (optional)</label>
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full mt-1 border border-stone-300 rounded p-2 text-sm h-24"
          placeholder="Wrong address, ordered by mistake, no longer needed…"
          data-testid="cancel-reason"
        />
        {error && <p className="text-xs text-rose-700 mt-2">{error}</p>}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary" data-testid="cancel-close">Keep order</button>
          <button onClick={submit} disabled={submitting}
            className="bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white px-4 py-2 rounded font-semibold text-sm"
            data-testid="cancel-submit">
            {submitting ? "Sending…" : "Request cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CustomerOrders() {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/orders/mine").then(r => setOrders(r.data)).catch(e => setError(e.response?.data?.detail || "Load error"));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="customer-orders-page">
      <Seo title="Your orders · The Farm To You" description="Track every order across every farm." canonical="/account/orders"/>
      <Link to="/account" className="text-sm text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] inline-flex items-center gap-1" data-testid="back-to-account">
        <ArrowLeft size={14}/> Back to account
      </Link>
      <p className="overline mt-4">Orders</p>
      <h1 className="font-display text-5xl mt-3">Your orders.</h1>
      <p className="text-sm text-[var(--ff-text-muted)] mt-2">Live tracking from every farm. Click an order for the full timeline.</p>

      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded mt-6 text-sm text-rose-800" data-testid="orders-error">{error}</div>}
      {!orders && !error && <div className="mt-10 text-[var(--ff-text-muted)]" data-testid="orders-loading">Loading…</div>}
      {orders && orders.length === 0 && (
        <div className="mt-10 card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="orders-empty">
          No orders yet — your first drop is just a click away.
          <div className="mt-4"><Link to="/drops" className="btn-primary">See current drops</Link></div>
        </div>
      )}

      <div className="mt-8 space-y-3">
        {orders?.map(o => {
          const stage = o.fulfillment_status || "paid";
          return (
            <Link key={o.id} to={`/account/orders/${o.id}`}
              className="block card-flat p-5 hover:border-[var(--ff-primary)] transition-colors" data-testid={`order-card-${o.id}`}>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-mono text-xs text-[var(--ff-text-muted)]">#{o.id.slice(0, 12)}</p>
                    <StatusChip stage={stage}/>
                    <span className="text-xs text-[var(--ff-text-muted)] inline-flex items-center gap-1"><Clock size={11}/> {o.created_at?.slice(0,10)}</span>
                  </div>
                  <p className="font-display text-lg mt-2">{(o.items || []).map(i => `${i.name} ×${i.qty}`).join(", ")}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] mt-1">{(o.items || []).length} item{o.items?.length === 1 ? "" : "s"} · ${(o.total || 0).toFixed(2)}</p>
                </div>
                <ChevronRight size={20} className="text-[var(--ff-text-muted)]"/>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ReviewForm({ vendorId, vendorName, orderId, items, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [productId, setProductId] = useState(items[0]?.product_id || "");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/marketplace/reviews", { rating, body, order_id: orderId, vendor_id: vendorId, product_id: productId || null });
      setOpen(false); onDone?.();
    } catch (e2) { setErr(e2.response?.data?.detail || "Could not submit review"); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="btn-secondary mt-3 text-xs" data-testid={`review-open-${vendorId}`}>★ Review {vendorName}</button>;
  return (
    <form onSubmit={submit} className="mt-3 bg-[var(--ff-muted)] rounded-lg p-4 space-y-3" data-testid={`review-form-${vendorId}`}>
      <div className="flex items-center gap-1">{[1,2,3,4,5].map(i => <button type="button" key={i} onClick={() => setRating(i)} className={`text-xl ${i <= rating ? "text-amber-400" : "text-stone-300"}`} data-testid={`review-star-${i}`}>★</button>)}</div>
      <select className="input-base" value={productId} onChange={e => setProductId(e.target.value)} data-testid="review-product"><option value="">The rancher overall</option>{items.map(it => <option key={it.product_id} value={it.product_id}>{it.name}</option>)}</select>
      <textarea className="input-base" rows={3} placeholder="How was it?" value={body} onChange={e => setBody(e.target.value)} data-testid="review-body" required minLength={3}/>
      {err && <p className="text-xs text-rose-700" data-testid="review-error">{err}</p>}
      <div className="flex gap-2"><button type="submit" className="btn-primary text-xs" data-testid="review-submit">Post verified review</button><button type="button" className="text-xs underline" onClick={() => setOpen(false)}>Cancel</button></div>
    </form>
  );
}

export function CustomerOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const reload = () => api.get(`/orders/${id}`).then(r => setOrder(r.data)).catch(e => setError(e.response?.data?.detail || "Load error"));
  useEffect(() => { reload(); }, [id]);

  if (error) return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Link to="/account/orders" className="text-sm underline" data-testid="back-link">← All orders</Link>
      <div className="mt-6 bg-rose-50 border border-rose-200 p-4 rounded text-sm text-rose-800" data-testid="order-error">{error}</div>
    </div>
  );
  if (!order) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="order-loading">Loading…</div>;

  const overall = order.fulfillment_status || "paid";
  const vendorIds = Array.from(new Set((order.items || []).map(i => i.vendor_id).filter(Boolean)));
  const vstatuses = order.vendor_statuses || {};
  const suborders = order.suborders || [];
  const addr = typeof order.shipping_address === "object" && order.shipping_address ? order.shipping_address : {};
  const cancellation = order.cancellation || null;
  const cancelOpenState = cancellation && ["requested", "approved", "refunded"].includes(cancellation.status);
  const canRequestCancel = !cancellation && overall !== "delivered" && overall !== "cancelled";

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="customer-order-detail">
      <Seo title={`Order #${id?.slice(0,12)} · The Farm To You`} description="Order tracking" canonical={`/account/orders/${id}`}/>
      <Link to="/account/orders" className="text-sm text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] inline-flex items-center gap-1" data-testid="back-link">
        <ArrowLeft size={14}/> All orders
      </Link>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <p className="overline">Order</p>
        <p className="font-mono text-sm text-[var(--ff-text-secondary)]" data-testid="order-id">#{id}</p>
        <StatusChip stage={overall}/>
      </div>
      <h1 className="font-display text-4xl mt-3">{(order.items || []).map(i => i.name).join(", ")}</h1>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1">Placed {order.created_at?.slice(0,10)} · {(order.items || []).length} item{order.items?.length === 1 ? "" : "s"} · ${(order.total || 0).toFixed(2)}</p>

      <div className="mt-8 card-flat p-6" data-testid="order-tracker-card">
        <p className="overline mb-4">{cancelOpenState ? "Cancellation" : "Status"}</p>
        {cancelOpenState ? (
          <>
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900" data-testid="cancel-inline-warning">
              We&apos;ll do our best to stop the shipment, but cancellations are <strong>not guaranteed</strong> once packing has begun.
            </div>
            <CancellationTracker cancellation={cancellation}/>
            {cancellation?.ops_note && (
              <p className="text-xs text-[var(--ff-text-muted)] mt-3">Ops note: {cancellation.ops_note}</p>
            )}
          </>
        ) : (
          <Tracker current={overall}/>
        )}
        {overall === "delivered" && order.delivery_photo_url && (
          <div className="mt-6 pt-6 border-t border-stone-200" data-testid="delivery-photo">
            <p className="text-sm font-semibold mb-2">Delivered photo</p>
            <a href={order.delivery_photo_url} target="_blank" rel="noreferrer">
              <img
                src={order.delivery_photo_url}
                alt="Carrier delivery photo"
                className="w-full max-w-md rounded-lg border border-stone-200 hover:opacity-90 transition-opacity"
                loading="lazy"
              />
            </a>
            <p className="text-xs text-[var(--ff-text-muted)] mt-2">Please bring your box inside — dry ice keeps meat safe for a few hours, not indefinitely.</p>
          </div>
        )}
      </div>

      {vendorIds.length > 0 && (
        <div className="mt-6" data-testid="vendor-shipments">
          <p className="overline mb-3">Shipments {vendorIds.length > 1 ? <span className="ml-2 text-[10px] text-amber-700">SPLIT SHIPMENT · {vendorIds.length} farms</span> : null}</p>
          <div className="space-y-3">
            {vendorIds.map(vid => {
              const items = (order.items || []).filter(i => i.vendor_id === vid);
              const so = suborders.find(x => x.vendor_id === vid);
              const SO_MAP = { sent_to_rancher: "paid", processing: "packed", shipped: "shipped", delivered: "delivered", canceled: "cancelled", refunded: "cancelled", placed: "paid" };
              const vs = so ? { status: SO_MAP[so.status] || so.status, tracking_url: so.tracking_url, carrier: so.carrier, tracking_number: so.tracking_number, updated_at: so.updated_at } : (vstatuses[vid] || { status: overall });
              const vendorName = so?.vendor_name || items[0]?.vendor_name || "Farm partner";
              return (
                <div key={vid} className="card-flat p-5" data-testid={`shipment-${vid}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-display text-lg">{vendorName}</p>
                    <StatusChip stage={vs.status || "paid"}/>
                    <Link to={`/ranchers/${vid}`} className="text-xs underline text-[var(--ff-text-muted)]" data-testid={`shipment-rancher-link-${vid}`}>Sold &amp; shipped by this rancher</Link>
                  </div>
                  {vs.tracking_number && <p className="text-xs mt-2">Tracking: <span className="font-mono">{vs.tracking_number}</span> {vs.carrier ? `(${vs.carrier})` : ""}</p>}
                  {so?.status === "delivered" && !(order.reviewed_keys || []).some(k => k.startsWith(vid + "|")) && (
                    <ReviewForm vendorId={vid} vendorName={vendorName} orderId={order.id} items={items} onDone={reload}/>
                  )}
                  <ul className="mt-3 text-sm text-[var(--ff-text-secondary)] space-y-1">
                    {items.map((it, i) => <li key={i}>{it.name} × {it.qty} <span className="text-xs text-[var(--ff-text-muted)] ml-2">${(it.price * it.qty).toFixed(2)}</span></li>)}
                  </ul>
                  {vs.tracking_url && (
                    <a href={vs.tracking_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-[var(--ff-primary)] underline" data-testid={`tracking-${vid}`}>
                      Track via {vs.carrier || "carrier"} <ExternalLink size={12}/>
                    </a>
                  )}
                  {vs.updated_at && <p className="text-xs text-[var(--ff-text-muted)] mt-2">Last update: {new Date(vs.updated_at).toLocaleString()}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(addr.line1 || addr.city) && (
        <div className="mt-6 card-flat p-5" data-testid="ship-address">
          <p className="overline">Shipping to</p>
          <p className="mt-2 text-sm">{order.shipping_name || addr.name || "—"}</p>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-1">
            {addr.line1}<br/>
            {addr.city}, {addr.state} {addr.postal_code}<br/>
            {addr.country}
          </p>
        </div>
      )}

      <div className="mt-10 card-flat p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="meal-plan-cta">
        <div>
          <p className="font-display text-lg">Cook plan for this box</p>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">Download a chef-curated PDF with ingredients, method, and timing.</p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/meal-plan.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="meal-plan-download-btn"
          onClick={(e) => {
            // Force fresh fetch with auth header by streaming via fetch + blob
            e.preventDefault();
            const token = localStorage.getItem("ff2d_token");
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/meal-plan.pdf`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            })
              .then((r) => r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`)))
              .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `meal-plan-${(id || "order").slice(0, 8)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              })
              .catch(() => { /* swallow — could surface a toast here */ });
          }}
        >
          <FileText size={14}/> Download meal plan
        </a>
      </div>

      <div className="mt-6 card-flat p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="problem-cta">
        <div>
          <p className="font-display text-lg">Something wrong with this order?</p>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">Damage, spoilage, or missing items — let&apos;s fix it.</p>
        </div>
        <button onClick={() => setReportOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="report-problem-btn">
          <AlertTriangle size={14}/> Report a problem
        </button>
      </div>

      {canRequestCancel && (
        <div className="mt-4 card-flat p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="cancel-cta">
          <div>
            <p className="font-display text-lg">Need to cancel this order?</p>
            <p className="text-sm text-[var(--ff-text-muted)] mt-1">We&apos;ll try to stop it before it ships. Cancellations aren&apos;t guaranteed once packing has begun.</p>
          </div>
          <button onClick={() => setCancelOpen(true)} className="border border-rose-300 text-rose-700 hover:bg-rose-50 px-4 py-2 rounded font-semibold text-sm inline-flex items-center gap-2" data-testid="cancel-request-btn">
            <Ban size={14}/> Request cancellation
          </button>
        </div>
      )}

      <CancelRequestModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        orderId={id}
        onSuccess={reload}
      />
      <ReportProblemModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        kind="customer_issue"
        orderId={id}
        topic="order"
        title="Tell us what went wrong"
        placeholder="Damage, spoilage, missing items — describe what happened. Reply to the confirmation email afterward to attach photos."
        cta="Open ticket"
      />
    </div>
  );
}
